import { GetLatestClasses } from "../../service/service";

/*
 * Data + pure helpers for the redesigned latest-classes page (handoff
 * README §6). Preferred source: GET /latest-feed — the full contract
 * (category chip+link, audio/video/both, duration, Hebrew+civil dates,
 * source-PDF flag) in one cached response. Until that deploys, the legacy
 * POST user/latest-classes payload degrades gracefully: media/duration from
 * legacy blocks, no category chips (the "New in" row shows All only), no
 * Hebrew headings.
 */

const BASE = process.env.REACT_APP_API || "https://api.insidechassidus.org/api";

const NEW_WINDOW_MS = 48 * 3600 * 1000;
export const FEED_PAGE = 25;
export const FEED_CAP = 100;

function parseLocalDate(value) {
  if (!value) return null;
  const s = String(value);
  return new Date(/^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T00:00:00` : s.replace(" ", "T"));
}

function normalizeItem(r) {
  return {
    id: r.id,
    title: r.title,
    slug: r.slug,
    // "category" is the container pill: a category, or the class's series
    // when it has no direct category link (type routes the pill's href).
    category: r.category
      ? { title: r.category.title, slug: r.category.slug, type: r.category.type || "category" }
      : null,
    media: r.media || null, // 'audio' | 'video' | 'both' | null
    durationSeconds: Number(r.duration_seconds) > 0 ? Number(r.duration_seconds) : null,
    hasSourcePdf: Boolean(r.has_source_pdf),
    civilDate: r.civil_date,
    hebrewDate: r.hebrew_date || null,
    postedAt: parseLocalDate(r.posted_at || r.civil_date),
  };
}

function normalizeLegacy(r) {
  if (!r?.id || !r?.title) return null;
  let hasAudio = false;
  let hasVideo = false;
  let duration = null;
  for (const b of r.block || []) {
    const t = (b.type || "").toLowerCase();
    if (t === "audio") {
      hasAudio = true;
      if (!duration && Number(b.duration) > 0) duration = Number(b.duration);
    }
    if (t === "video") hasVideo = true;
  }
  const civil = r.is_publish_date || (r.created_at ? String(r.created_at).slice(0, 10) : null);
  if (!civil) return null;
  return {
    id: r.id,
    title: r.title,
    slug: r.custom_permalink && r.custom_permalink !== "None" ? r.custom_permalink : r.slug,
    category: null,
    media: hasAudio && hasVideo ? "both" : hasVideo ? "video" : hasAudio ? "audio" : null,
    durationSeconds: duration,
    hasSourcePdf: false,
    civilDate: civil,
    hebrewDate: null,
    postedAt: parseLocalDate(civil),
  };
}

// Newest-first by posted date, enforced client-side: the legacy fallback
// orders by row-creation date, and out-of-order civil dates would split
// into duplicate day groups ("Wednesday … Wednesday"). Stable sort — rows
// within one day keep their source order.
function sortNewestFirst(items) {
  return [...items].sort(
    (a, b) =>
      String(b.civilDate).localeCompare(String(a.civilDate)) ||
      (b.postedAt?.getTime() || 0) - (a.postedAt?.getTime() || 0)
  );
}

/**
 * The /latest-feed contract only — null when the endpoint isn't available.
 * Used directly by surfaces that enrich another data source with feed
 * fields (e.g. the homepage's latest-classes shelf pills) and shouldn't
 * trigger the legacy fallback's duplicate request.
 */
export async function fetchFeedItemsOnly() {
  try {
    const res = await fetch(`${BASE}/latest-feed`, { headers: { Accept: "application/json" } });
    if (res.ok) {
      const json = await res.json();
      if (json?.status && Array.isArray(json.data?.items)) {
        return json.data.items.map(normalizeItem);
      }
    }
  } catch (_) {
    /* endpoint unavailable */
  }
  return null;
}

export async function getLatestFeed() {
  const items = await fetchFeedItemsOnly();
  if (items) return sortNewestFirst(items);
  try {
    const res = await GetLatestClasses();
    if (res?.status && Array.isArray(res.data)) {
      return sortNewestFirst(res.data.map(normalizeLegacy).filter(Boolean)).slice(0, FEED_CAP);
    }
  } catch (_) {
    /* designed empty state */
  }
  return [];
}

export function isNewItem(item, now = new Date()) {
  return Boolean(item.postedAt) && now - item.postedAt >= 0 && now - item.postedAt < NEW_WINDOW_MS;
}

/** Today / Yesterday / weekday (≤6 days) / "Last week" / "N weeks ago". */
export function dayLabel(civilDate, today = new Date()) {
  const day = parseLocalDate(civilDate);
  if (!day) return "";
  const start = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((start(today) - start(day)) / 86400000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days <= 6) return day.toLocaleDateString("en-US", { weekday: "long" });
  const weeks = Math.floor(days / 7);
  return weeks <= 1 ? "Last week" : `${weeks} weeks ago`;
}

/**
 * Group a (newest-first) item list into consecutive day groups.
 * Day-level groups carry the Hebrew date heading; Today also appends the
 * civil date ("27 Tammuz 5786 · July 12, 2026"); multi-day week buckets
 * show no date (they span several).
 */
export function groupFeed(items, today = new Date()) {
  const groups = [];
  for (const item of items) {
    const label = dayLabel(item.civilDate, today);
    const last = groups[groups.length - 1];
    if (last && last.label === label) {
      last.rows.push(item);
      continue;
    }
    const isDayLevel = label === "Today" || label === "Yesterday" || !label.includes("week");
    let heading = isDayLevel ? item.hebrewDate || "" : "";
    if (label === "Today" && heading) {
      const civil = parseLocalDate(item.civilDate).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
      heading = `${heading} · ${civil}`;
    }
    groups.push({ label, hebrewDate: heading, rows: [item] });
  }
  return groups;
}

/**
 * "New in" facets from the feed itself: one entry per category that
 * received classes in the window, with its count, ordered count-desc.
 */
export function facetCounts(items) {
  const bySlug = new Map();
  for (const item of items) {
    if (!item.category) continue;
    const cur = bySlug.get(item.category.slug);
    if (cur) cur.count += 1;
    else bySlug.set(item.category.slug, { slug: item.category.slug, title: item.category.title, count: 1 });
  }
  return [...bySlug.values()].sort((a, b) => b.count - a.count || a.title.localeCompare(b.title));
}
